/* Waggle Enrich — collection.js
   Collection and search pages: filter drawer form and sort select update results with fetch + DOMParser
   through the Section Rendering API (URL kept in sync with history.replaceState), removable chips,
   grid density toggle (2/3/4 desktop, 1/2 mobile, remembered), and "Load more". Without JS the same
   form submits normally and numbered pagination works. */

const W = window.Waggle;
const doc = document;
const parser = new DOMParser();
const STORAGE_KEY = 'waggle:density';

const readDensity = () => {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {};
  } catch (error) {
    return {};
  }
};

const initCollection = (root) => {
  const sectionId = root.dataset.sectionId;
  const form = () => root.querySelector('[data-facets-form]');
  const results = () => root.querySelector('[data-results]');
  let controller = null;

  /* ---------- Density ---------- */
  const applyDensity = ({ d = 3, m = 2 } = {}) => {
    const grid = root.querySelector('[data-grid]');
    if (grid) {
      grid.style.setProperty('--cols-d', d);
      grid.style.setProperty('--cols-m', m);
    }
    W.qsa('[data-density-d]', root).forEach((button) => button.setAttribute('aria-pressed', String(Number(button.dataset.densityD) === Number(d))));
    W.qsa('[data-density-m]', root).forEach((button) => button.setAttribute('aria-pressed', String(Number(button.dataset.densityM) === Number(m))));
  };
  let density = { d: 3, m: 2, ...readDensity() };
  applyDensity(density);

  root.addEventListener('click', (event) => {
    const button = event.target.closest('[data-density-d], [data-density-m]');
    if (!button) return;
    if (button.dataset.densityD) density = { ...density, d: Number(button.dataset.densityD) };
    if (button.dataset.densityM) density = { ...density, m: Number(button.dataset.densityM) };
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(density));
    } catch (error) {
      /* Private mode: the toggle still works for this page. */
    }
    const grid = root.querySelector('[data-grid]');
    W.animate(grid, [{ opacity: 0.35 }, { opacity: 1 }], { duration: 260, easing: W.ease.standard });
    applyDensity(density);
  });

  /* ---------- Section Rendering ---------- */
  const swap = (fresh, selector) => {
    const next = fresh.querySelector(selector);
    const current = root.querySelector(selector);
    if (next && current) current.replaceChildren(...next.childNodes);
  };

  const render = async (url) => {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    if (controller) controller.abort();
    controller = new AbortController();
    const box = results();
    if (box) {
      box.classList.add('is-loading');
      box.setAttribute('aria-busy', 'true');
    }
    const focusedId = doc.activeElement && doc.activeElement.id;
    try {
      const response = await fetch(target.toString(), { signal: controller.signal });
      const html = await response.text();
      const fresh = parser.parseFromString(html, 'text/html');
      swap(fresh, '[data-results]');
      swap(fresh, '[data-active-filters-wrap]');
      swap(fresh, '[data-facets-wrap]');
      const freshCount = fresh.querySelector('[data-results-count]');
      const count = root.querySelector('[data-results-count]') || doc.querySelector('[data-results-count]');
      if (freshCount && count) count.textContent = freshCount.textContent;
      target.searchParams.delete('section_id');
      window.history.replaceState(window.history.state, '', `${target.pathname}${target.search}`);
      applyDensity(density);
      if (W.motion) W.motion.observe(root);
      W.syncSteppers && W.syncSteppers(root);
      W.qsa('a[data-close]', root).forEach((link) => link.setAttribute('role', 'button'));
      if (focusedId) {
        const again = doc.getElementById(focusedId);
        if (again && again !== doc.activeElement) again.focus({ preventScroll: true });
      }
      W.announce(W.strings.resultsUpdated);
    } catch (error) {
      if (error.name !== 'AbortError') window.location.href = url;
    } finally {
      const after = results();
      if (after) {
        after.classList.remove('is-loading');
        after.removeAttribute('aria-busy');
      }
    }
  };

  const urlFromForm = () => {
    const current = form();
    if (!current) return window.location.href;
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(current)) {
      if (String(value).trim() !== '') params.append(key, value);
    }
    return `${current.getAttribute('action').split('?')[0]}?${params.toString()}`;
  };

  let debounce = null;
  root.addEventListener('change', (event) => {
    const field = event.target;
    const current = form();
    if (!current) return;
    const belongs = field.form === current || current.contains(field);
    if (!belongs) return;
    clearTimeout(debounce);
    debounce = setTimeout(() => render(urlFromForm()), field.type === 'number' ? 600 : 250);
  });
  root.addEventListener('submit', (event) => {
    if (!event.target.matches('[data-facets-form]')) return;
    event.preventDefault();
    clearTimeout(debounce);
    render(urlFromForm());
    const dialog = event.target.closest('dialog');
    if (dialog) W.overlays.close(dialog.id);
  });
  root.addEventListener('click', (event) => {
    const link = event.target.closest('[data-facet-link], [data-facets-clear]');
    if (!link) return;
    event.preventDefault();
    render(link.href);
  });

  /* ---------- Load more ---------- */
  root.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-load-more]');
    if (!button) return;
    event.preventDefault();
    const next = new URL(button.dataset.nextUrl, window.location.origin);
    next.searchParams.set('section_id', sectionId);
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(next.toString());
      const fresh = parser.parseFromString(await response.text(), 'text/html');
      const grid = root.querySelector('[data-grid]');
      const incoming = W.qsa('[data-grid] > .grid__item:not(.grid__item--editorial)', fresh);
      incoming.forEach((item) => {
        if (item.querySelector('.plate img')) grid.appendChild(item);
      });
      const pager = root.querySelector('[data-pager]');
      const freshPager = fresh.querySelector('[data-pager]');
      if (pager && freshPager) pager.replaceWith(freshPager);
      else if (pager) pager.remove();
      if (W.motion) W.motion.observe(root);
      const firstNew = incoming[0] && incoming[0].querySelector('a');
      if (firstNew) firstNew.focus({ preventScroll: true });
    } catch (error) {
      window.location.href = button.dataset.nextUrl;
    }
  });
};

W.qsa('[data-collection]').forEach(initCollection);
